import { readonly, ref, useAttrs, watch } from "vue";
import { isPresent } from "#ce/utils/props";

/**
 * The checked state of a boolean control: the live checkedness, the default a form reset restores,
 * and the dirty flag that decides which of the two the attribute may move.
 *
 * `checked` is deliberately not a declared prop. Vue installs a non-configurable accessor on the
 * host for every declared prop, which cannot be replaced by the live `checked` accessor the control
 * has to expose, so the attribute is read through `attrs` instead.
 *
 * Observable state is readonly: every transition runs through `setChecked` or `reset`, so a direct
 * write would leave the dirty flag behind and let a later attribute change overwrite it.
 *
 * @returns {{
 *   checked: Readonly<import('vue').Ref<Boolean>>,
 *   defaultChecked: Readonly<import('vue').Ref<Boolean>>,
 *   setChecked: (value: *) => void,
 *   reset: () => void,
 * }}
 */
export function useCheckedModel() {
  const attrs = useAttrs();

  const defaultChecked = ref(isPresent(attrs.checked));
  const checked = ref(defaultChecked.value);

  // The dirty checkedness flag of the HTML forms model: a user toggle or a write to the `checked`
  // property pins the state, so the attribute stops moving it. Only a form reset clears it.
  const isDirty = ref(false);

  // The `checked` content attribute names the default, so it moves the state only while the
  // control is clean — it never sets the dirty flag itself.
  watch(() => attrs.checked, setDefaultChecked);

  function setChecked(value) {
    checked.value = isPresent(value);
    isDirty.value = true;
  }

  function setDefaultChecked(value) {
    defaultChecked.value = isPresent(value);

    if (!isDirty.value) {
      checked.value = defaultChecked.value;
    }
  }

  function reset() {
    checked.value = defaultChecked.value;
    isDirty.value = false;
  }

  return { checked: readonly(checked), defaultChecked: readonly(defaultChecked), setChecked, reset };
}
